import { Flex, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { UploadProps } from 'antd/lib/upload';

interface CustomUploadProps extends Omit<UploadProps, 'type'> {
  type?: 'image' | 'audio' | 'video' | 'file';
  text?: string;
  className?: string;
  uploadProps?: Partial<UploadProps>;
}

/**
 * 通用 Upload 組件，包裝 Antd Upload
 */
export const CustomUpload: React.FC<CustomUploadProps> = ({
  type = 'file',
  text,
  onChange,
  fileList = [],
  maxCount = 1,
  listType,
  className = '',
  uploadProps = {},
  ...restProps
}) => {
  // 根據類型設定預設配置
  const getTypeConfig = (uploadType: CustomUploadProps['type']) => {
    switch (uploadType) {
      case 'image':
        return {
          accept: 'image/*',
          defaultText: '上傳圖片',
          listType: 'picture' as const,
        }
      case 'audio':
        return {
          accept: 'audio/*',
          defaultText: '上傳音檔',
          listType: 'text' as const,
        }
      case 'video':
        return {
          accept: 'video/*',
          defaultText: '上傳影片',
          listType: 'text' as const,
        }
      default:
        return {
          accept: '*',
          defaultText: '上傳檔案',
          listType: 'text' as const,
        };
    }
  };

  const typeConfig = getTypeConfig(type);
  const displayText = text || typeConfig.defaultText;
  const finalListType = listType || typeConfig.listType;

  // 統一的上傳配置
  const defaultUploadProps: UploadProps = {
    beforeUpload: () => false, // 防止自動上傳
    showUploadList: true,
    maxCount,
    accept: typeConfig.accept,
    listType: finalListType,
    ...uploadProps
  };

  return (
    <Upload
      className={`
        [&_.ant-upload-wrapper]:min-w-[200px]
        [&_.ant-upload-wrapper]:!w-full
        [&_.ant-upload]:!min-w-[200px]
        [&_.ant-upload]:!w-full
        ${className}
      `}
      fileList={fileList}
      onChange={onChange}
      {...defaultUploadProps}
      {...restProps}
    >
      {/* 當已有檔案且達到最大數量時，不顯示上傳區域 */}
      {(!fileList || fileList.length < maxCount) && (
        <Flex
          justify="space-between"
          align="center"
          className="w-full border border-dashed border-[#EEEEEE] rounded p-4 cursor-pointer hover:border-[#007AFF] transition-colors"
        >
          <span className="text-xs text-[#007AFF]">{displayText}</span>
          <UploadOutlined className="text-[#007AFF]" />
        </Flex>
      )}
    </Upload>
  );
};